import { FONT_FAMILIES } from '@/constant';
import { SlideComponentSentenceMaker } from '@/types/slide';
import { findAllMultipleChinesePhrases } from '@/utils/chinese';
import { Box, Typography } from '@mui/material';
import React from 'react';

const TRADITIONAL_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZHKXinBiaoKai')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;
const SIMPLIFIED_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZKai-Z03S')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;

interface GridProps {
    cols: number;
    rows: number;
    language: SlideComponentSentenceMaker['content']['language'];
    text?: string;
    gridSizeY?: number;
    keywords?: string[];
}

export const Grid: React.FC<GridProps> = ({ cols, rows, language, text = '', gridSizeY, keywords }) => {
    const chars = text.split('');
    // by reading the gridSizeY, we can determine the font size to ensure the characters fit well within the grid cells
    const fontSize = gridSizeY ? `${gridSizeY * 0.5}px` : '2rem';

    const highlightedIndices = new Set<number>();
    if (keywords && keywords.length > 0) {
        const phraseIndicesMap = findAllMultipleChinesePhrases(text, keywords);
        for (const indicesArray of phraseIndicesMap.values()) {
            if (indicesArray) {
                indicesArray.forEach((indices) => {
                    // Add all indices for this phrase occurrence to the highlighted set
                    indices.forEach((index) => highlightedIndices.add(index));
                });
            }
        }
    }

    const isHighlighted = (index: number) => highlightedIndices.has(index);

    const textIsNotEmpty = text.trim().length > 0;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
                backgroundColor: (theme) => (theme.vars || theme).palette.grey[200],
            }}
        >
            {Array.from({ length: cols * rows }).map((_, i) => {
                const char = chars[i];
                return (
                    <Box
                        key={i}
                        sx={{
                            border: '0.5px solid',
                            borderColor: (theme) => (theme.vars || theme).palette.grey[300],
                            backgroundColor: isHighlighted(i) ? '#FFE3B0' : textIsNotEmpty ? 'white' : 'transparent',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            aspectRatio: 1,
                        }}
                    >
                        {char && (
                            <Typography
                                sx={{
                                    fontFamily: language === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY,
                                    fontWeight: 500,
                                    color: (theme) => theme.palette.common.black,
                                    fontSize: fontSize,
                                }}
                            >
                                {char}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};
